/**
 * Color utility functions for working with colors across the application
 */

/**
 * Calculate relative luminance using W3C WCAG standard
 * https://www.w3.org/TR/2008/REC-WCAG20-20081211/#relativeluminancedef
 *
 * Direct translation of app/services/color_utils.rb#color_relative_luminance
 *
 * @param {number} r - Red value (0-255)
 * @param {number} g - Green value (0-255)
 * @param {number} b - Blue value (0-255)
 * @returns {number} Relative luminance value (0-1)
 */
export function calculateRelativeLuminance(r, g, b) {
  // Convert RGB values from 0-255 to 0-1
  const rSrgb = r / 255.0
  const gSrgb = g / 255.0
  const bSrgb = b / 255.0

  // Convert sRGB values to linear RGB
  const rLinear = rSrgb <= 0.03928 ? rSrgb / 12.92 : Math.pow((rSrgb + 0.055) / 1.055, 2.4)
  const gLinear = gSrgb <= 0.03928 ? gSrgb / 12.92 : Math.pow((gSrgb + 0.055) / 1.055, 2.4)
  const bLinear = bSrgb <= 0.03928 ? bSrgb / 12.92 : Math.pow((bSrgb + 0.055) / 1.055, 2.4)

  // Calculate relative luminance
  return 0.2126 * rLinear + 0.7152 * gLinear + 0.0722 * bLinear
}

/**
 * Determine if a background color is dark based on relative luminance
 *
 * @param {string} backgroundColor - CSS color string (e.g., 'rgb(255, 255, 255)')
 * @returns {boolean} True if the background is considered dark
 */
export function isBackgroundColorDark(backgroundColor) {
  // Extract RGB values from the color string
  const rgbMatch = backgroundColor.match(/rgba?\((\d+),\s*(\d+),\s*(\d+)/)
  if (!rgbMatch) {
    return false
  }

  const [, r, g, b] = rgbMatch.map(Number)
  const relativeLuminance = calculateRelativeLuminance(r, g, b)

  return relativeLuminance < 0.5
}

// WCAG 2.1 AA contrast thresholds — mirror app/services/color_utils.rb.
export const AA_NORMAL_TEXT = 4.5
export const AA_LARGE_TEXT = 3.0

/**
 * Parse a CSS color string into {r, g, b, a}. Handles the rgb()/rgba() form that
 * getComputedStyle always returns, plus #rgb / #rrggbb hex. Returns null if unparseable.
 *
 * @param {string} color
 * @returns {{r: number, g: number, b: number, a: number}|null}
 */
export function parseRgb(color) {
  if (!color) return null

  const rgbMatch = color.match(/rgba?\(\s*(\d+)[,\s]+(\d+)[,\s]+(\d+)(?:[,\s/]+([\d.]+))?/i)
  if (rgbMatch) {
    const [, r, g, b, a] = rgbMatch
    return { r: Number(r), g: Number(g), b: Number(b), a: a === undefined ? 1 : Number(a) }
  }

  let hex = color.trim().replace(/^#/, '')
  if (/^[0-9a-f]{3}$/i.test(hex)) hex = hex.split('').map((c) => c + c).join('')
  if (/^[0-9a-f]{6}$/i.test(hex)) {
    return {
      r: parseInt(hex.slice(0, 2), 16),
      g: parseInt(hex.slice(2, 4), 16),
      b: parseInt(hex.slice(4, 6), 16),
      a: 1,
    }
  }

  return null
}

/**
 * WCAG contrast ratio between two relative luminances (0-1). Range 1..21.
 * Mirror of app/services/color_utils.rb#relative_luminance_contrast_ratio.
 *
 * @param {number} luminanceA
 * @param {number} luminanceB
 * @returns {number}
 */
export function relativeLuminanceContrastRatio(luminanceA, luminanceB) {
  const lighter = Math.max(luminanceA, luminanceB)
  const darker = Math.min(luminanceA, luminanceB)
  return (lighter + 0.05) / (darker + 0.05)
}

/**
 * WCAG contrast ratio between two CSS colors. Alpha is ignored (matching the Ruby
 * ColorUtils); composite backgrounds before calling if a layer is translucent.
 *
 * @param {string} colorA
 * @param {string} colorB
 * @returns {number|null} 1..21, or null if either color is unparseable
 */
export function contrastRatio(colorA, colorB) {
  const a = parseRgb(colorA)
  const b = parseRgb(colorB)
  if (!a || !b) return null

  return relativeLuminanceContrastRatio(
    calculateRelativeLuminance(a.r, a.g, a.b),
    calculateRelativeLuminance(b.r, b.g, b.b),
  )
}

/**
 * Whether foreground/background meet WCAG 2.1 AA contrast.
 * Normal text needs 4.5:1, large text (>=18pt, or >=14pt bold) needs 3:1.
 *
 * @param {string} fg
 * @param {string} bg
 * @param {{large?: boolean}} [opts]
 * @returns {boolean}
 */
export function meetsAaContrast(fg, bg, { large = false } = {}) {
  const ratio = contrastRatio(fg, bg)
  if (ratio === null) return true // don't flag colors we can't parse

  return ratio >= (large ? AA_LARGE_TEXT : AA_NORMAL_TEXT)
}
